import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Building2, Trees, DoorOpen, Sofa, CookingPot, UtensilsCrossed, Wine,
  BedDouble, BedSingle, Bath, ShowerHead, Droplets, Flower2, Car,
  CircleParking, Plane, Camera, Upload, Check, ChevronRight, ChevronLeft,
  Video, X, Wifi, WifiOff, Loader2, Play, FolderOpen, AlertCircle, Plus, Trash2,
  Gauge, Copy, CheckCircle, ScanLine
} from 'lucide-react';
import './CaptureApp.css';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
const MIN_DURATION = 5; // seconds
const MAX_DURATION = 15; // seconds
const SPEED_THRESHOLD_MBPS = 2; // Below this = slow

// Check video duration using HTML5 Video API
function getVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(null);
    };
    video.src = URL.createObjectURL(file);
  });
}

const ROOM_ICONS = {
  'building-2': Building2, 'trees': Trees, 'door-open': DoorOpen,
  'sofa': Sofa, 'cooking-pot': CookingPot, 'utensils-crossed': UtensilsCrossed,
  'wine': Wine, 'bed-double': BedDouble, 'bed-single': BedSingle,
  'bath': Bath, 'shower-head': ShowerHead, 'droplets': Droplets,
  'flower-2': Flower2, 'car': Car, 'circle-parking': CircleParking,
  'plane': Plane,
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatTime(seconds) {
  if (!seconds || seconds === Infinity) return '--';
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.ceil(seconds % 60)}s`;
}

// ── Speed Test (download 500KB and measure) ──
async function measureSpeed() {
  try {
    const start = performance.now();
    const resp = await fetch(`${API}/api/capture/speed-test`, { cache: 'no-store' });
    const blob = await resp.blob();
    const elapsed = (performance.now() - start) / 1000; // seconds
    const sizeBytes = blob.size || 500 * 1024;
    const mbps = (sizeBytes * 8) / (elapsed * 1000000); // Megabits per second
    return Math.round(mbps * 10) / 10;
  } catch {
    return null;
  }
}


// ── Speed Indicator (always visible in header) ──
function SpeedIndicator({ mbps, testing, onTap }) {
  if (testing) {
    return (
      <button className="cap-speed-indicator cap-speed-testing" onClick={onTap} data-testid="cap-speed-indicator">
        <Loader2 size={13} className="spin" />
        <span>Testing...</span>
      </button>
    );
  }
  if (mbps === null) {
    return (
      <button className="cap-speed-indicator cap-speed-unknown" onClick={onTap} data-testid="cap-speed-indicator">
        <WifiOff size={13} />
        <span>No signal</span>
      </button>
    );
  }
  const isSlow = mbps < SPEED_THRESHOLD_MBPS;
  return (
    <button
      className={`cap-speed-indicator ${isSlow ? 'cap-speed-slow' : 'cap-speed-fast'}`}
      onClick={onTap}
      data-testid="cap-speed-indicator"
    >
      <Gauge size={13} />
      <span>{mbps} Mbps</span>
      <Wifi size={11} />
    </button>
  );
}


// ── WiFi Password Scanner Modal ──
function WifiWarningModal({ mbps, onDone }) {
  const [scanning, setScanning] = useState(false);
  const [extractedText, setExtractedText] = useState(null);
  const [copied, setCopied] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const fileInputRef = useRef(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setScanning(true);
    setExtractedText(null);
    setOcrError(null);
    setCopied(false);

    try {
      // Use Tesseract.js for client-side OCR
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker('eng');
      const { data: { text } } = await worker.recognize(file);
      await worker.terminate();

      if (text && text.trim().length > 0) {
        // Try to find WiFi password patterns
        const password = extractWifiPassword(text);
        setExtractedText(password || text.trim());

        // Auto-copy to clipboard
        try {
          await navigator.clipboard.writeText(password || text.trim());
          setCopied(true);
          toast.success('Password copied to clipboard!');
        } catch {
          // Clipboard may not be available
        }
      } else {
        setOcrError('No text found in image. Try getting closer to the label.');
      }
    } catch (err) {
      console.error('OCR error:', err);
      setOcrError('Could not read the image. Try again with a clearer photo.');
    } finally {
      setScanning(false);
    }
  };

  const handleCopy = async () => {
    if (!extractedText) return;
    try {
      await navigator.clipboard.writeText(extractedText);
      setCopied(true);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Could not copy — long-press to select and copy manually');
    }
  };

  const isSlow = mbps !== null && mbps < SPEED_THRESHOLD_MBPS;

  return (
    <div className="cap-wifi-overlay" data-testid="cap-wifi-modal">
      <div className="cap-wifi-modal">
        {/* Warning header */}
        <div className={`cap-wifi-header ${isSlow ? 'cap-wifi-header-slow' : 'cap-wifi-header-ok'}`}>
          {isSlow ? <WifiOff size={28} /> : <Wifi size={28} />}
          <div className="cap-wifi-header-text">
            {isSlow ? (
              <>
                <span className="cap-wifi-title">Slow Connection</span>
                <span className="cap-wifi-speed">{mbps} Mbps — video uploads may be very slow</span>
              </>
            ) : (
              <>
                <span className="cap-wifi-title">Join WiFi</span>
                <span className="cap-wifi-speed">{mbps} Mbps — optionally connect to faster WiFi</span>
              </>
            )}
          </div>
        </div>

        <div className="cap-wifi-body">
          <p className="cap-wifi-hint">
            {isSlow
              ? 'Ask for the WiFi password or photograph the router label to copy it.'
              : 'Photograph the router label to copy the WiFi password.'}
          </p>

          {/* OCR result */}
          {extractedText && (
            <div className="cap-wifi-result" data-testid="cap-wifi-result">
              <div className="cap-wifi-password-wrap">
                <span className="cap-wifi-password-label">Detected text:</span>
                <div className="cap-wifi-password-value" data-testid="cap-wifi-password">{extractedText}</div>
              </div>
              <button
                className={`cap-wifi-copy-btn ${copied ? 'cap-wifi-copied' : ''}`}
                onClick={handleCopy}
                data-testid="cap-wifi-copy"
              >
                {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}

          {ocrError && (
            <div className="cap-wifi-error" data-testid="cap-wifi-error">
              <AlertCircle size={14} />
              <span>{ocrError}</span>
            </div>
          )}

          {scanning && (
            <div className="cap-wifi-scanning" data-testid="cap-wifi-scanning">
              <Loader2 size={18} className="spin" />
              <span>Reading text from image...</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="cap-wifi-actions">
            <label className={`cap-wifi-cam-btn ${scanning ? 'cap-wifi-cam-disabled' : ''}`} data-testid="cap-wifi-cam-btn">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={handleCapture}
                disabled={scanning}
              />
              <Camera size={22} />
              <span>Scan Password</span>
            </label>
            <button className="cap-wifi-done-btn" onClick={onDone} data-testid="cap-wifi-done-btn">
              <Check size={18} />
              <span>Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: try to extract WiFi password from OCR text
function extractWifiPassword(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  // Look for common patterns: "Password: xxx", "Key: xxx", "WiFi: xxx", "Pass: xxx"
  for (const line of lines) {
    const match = line.match(/(?:password|pass|key|wifi\s*(?:key|pass(?:word)?))\s*[:=]\s*(.+)/i);
    if (match && match[1]?.trim()) {
      return match[1].trim();
    }
  }
  // Look for "WPA" or "WEP" key line
  for (const line of lines) {
    const match = line.match(/(?:wpa|wep|wpa2|network)\s*(?:key)?\s*[:=]\s*(.+)/i);
    if (match && match[1]?.trim()) {
      return match[1].trim();
    }
  }
  return null;
}


// ── Step 1: Brand → Branch → Property Selection ──
function PropertySelector({ onSelect }) {
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('client'); // client | branch | property

  useEffect(() => {
    axios.get(`${API}/api/capture/clients`).then(r => setClients(r.data.clients || []));
  }, []);

  const selectClient = (c) => {
    setSelectedClient(c);
    setStep('branch');
    setLoading(true);
    axios.get(`${API}/api/capture/branches/${c.id}`).then(r => {
      setBranches(r.data.branches || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const selectBranch = (b) => {
    setSelectedBranch(b);
    setStep('property');
    setLoading(true);
    axios.get(`${API}/api/capture/properties/${b.id}`).then(r => {
      setProperties(r.data.properties || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const goBack = () => {
    if (step === 'property') { setStep('branch'); setProperties([]); }
    else if (step === 'branch') { setStep('client'); setBranches([]); setSelectedClient(null); }
  };

  return (
    <div className="cap-selector" data-testid="cap-selector">
      <div className="cap-selector-header">
        {step !== 'client' && (
          <button className="cap-back" onClick={goBack} data-testid="cap-back">
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="cap-selector-title">
          {step === 'client' && 'Select Brand'}
          {step === 'branch' && selectedClient?.name}
          {step === 'property' && selectedBranch?.name}
        </div>
      </div>

      {step === 'client' && (
        <div className="cap-list" data-testid="cap-client-list">
          {clients.map(c => (
            <button key={c.id} className="cap-list-item" onClick={() => selectClient(c)} data-testid={`cap-client-${c.id}`}>
              <Building2 size={18} />
              <span>{c.name}</span>
              <ChevronRight size={16} className="cap-list-arrow" />
            </button>
          ))}
          {clients.length === 0 && <div className="cap-empty">No brands found</div>}
        </div>
      )}

      {step === 'branch' && (
        <div className="cap-list" data-testid="cap-branch-list">
          {loading ? <div className="cap-loading"><Loader2 size={20} className="spin" /> Loading branches...</div> :
            branches.map(b => (
              <button key={b.id} className="cap-list-item" onClick={() => selectBranch(b)} data-testid={`cap-branch-${b.id}`}>
                <Building2 size={18} />
                <span>{b.name}</span>
                <ChevronRight size={16} className="cap-list-arrow" />
              </button>
            ))
          }
          {!loading && branches.length === 0 && <div className="cap-empty">No branches found</div>}
        </div>
      )}

      {step === 'property' && (
        <div className="cap-list" data-testid="cap-property-list">
          {loading ? <div className="cap-loading"><Loader2 size={20} className="spin" /> Loading properties...</div> :
            properties.map(p => (
              <button key={p.order_id} className="cap-list-item" onClick={() => onSelect(selectedClient, selectedBranch, p)} data-testid={`cap-prop-${p.order_id}`}>
                <FolderOpen size={18} />
                <div className="cap-prop-info">
                  <span className="cap-prop-address">{p.address}</span>
                  <span className="cap-prop-id">#{p.order_id}</span>
                </div>
                <ChevronRight size={16} className="cap-list-arrow" />
              </button>
            ))
          }
          {!loading && properties.length === 0 && <div className="cap-empty">No properties found for this branch</div>}
        </div>
      )}
    </div>
  );
}


// ── Step 2: Room Grid ──
function RoomGrid({ rooms, selectedRooms, onToggle, onStart }) {
  return (
    <div className="cap-rooms" data-testid="cap-rooms">
      <p className="cap-rooms-hint">Select the rooms you'll be filming</p>
      <div className="cap-room-grid">
        {rooms.map(room => {
          const Icon = ROOM_ICONS[room.icon] || Video;
          const selected = selectedRooms.includes(room.key);
          return (
            <button
              key={room.key}
              className={`cap-room-btn ${selected ? 'cap-room-selected' : ''}`}
              onClick={() => onToggle(room.key)}
              data-testid={`cap-room-${room.key}`}
            >
              <Icon size={24} />
              <span>{room.name}</span>
              {selected && <div className="cap-room-check"><Check size={14} /></div>}
            </button>
          );
        })}
      </div>
      <button
        className="cap-start-btn"
        onClick={onStart}
        disabled={selectedRooms.length === 0}
        data-testid="cap-start-filming"
      >
        <Camera size={18} />
        Start Filming ({selectedRooms.length} room{selectedRooms.length !== 1 ? 's' : ''})
      </button>
    </div>
  );
}


// ── Upload Queue Item ──
function UploadItem({ item, onRetry, onDelete, onReshoot, onUploadAnyway }) {
  const pct = item.status === 'complete' ? 100 :
    item.status === 'uploading' ? Math.round((item.chunksUploaded / item.totalChunks) * 100) : 0;
  const canDelete = item.status === 'complete' || item.status === 'failed' || item.status === 'waiting';
  const isRejected = item.status === 'rejected';
  // Only short-clip rejects can be overridden — too-long clips still need reshoot.
  const canOverride = isRejected && item.rejectReason === 'Minimum Clip Length Not Met';

  if (isRejected) {
    return (
      <div className="cap-upload-item cap-upload-rejected" data-testid={`cap-upload-${item.id}`}>
        <div className="cap-rejected-content">
          <AlertCircle size={20} className="cap-rejected-icon" />
          <div className="cap-rejected-text">
            <span className="cap-rejected-title">{item.rejectReason}</span>
            <span className="cap-rejected-detail">
              {item.filename} &middot; {item.duration ? `${item.duration.toFixed(1)}s` : ''} &middot; {formatBytes(item.size)}
            </span>
          </div>
        </div>
        <div className="cap-rejected-actions">
          {canOverride && onUploadAnyway && (
            <button
              type="button"
              className="cap-upload-anyway-btn"
              onClick={() => onUploadAnyway(item)}
              data-testid={`cap-upload-anyway-${item.id}`}
            >
              <Upload size={14} />
              <span>Upload anyway</span>
            </button>
          )}
          <label className="cap-reshoot-btn" data-testid={`cap-reshoot-${item.id}`}>
            <Camera size={14} />
            <span>Reshoot</span>
            <input
              type="file"
              accept="video/*"
              hidden
              onChange={e => { if (e.target.files?.length) onReshoot(item, e.target.files); e.target.value = ''; }}
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className={`cap-upload-item cap-upload-${item.status}`} data-testid={`cap-upload-${item.id}`}>
      <div className="cap-upload-info">
        <span className="cap-upload-name">{item.filename}</span>
        <span className="cap-upload-meta">
          {formatBytes(item.size)}
          {item.duration && <> &middot; {item.duration.toFixed(1)}s</>}
          {item.status === 'uploading' && item.speed > 0 && (
            <> &middot; {formatBytes(item.speed)}/s &middot; ETA {formatTime(item.eta)}</>
          )}
          {item.status === 'complete' && <> &middot; Done</>}
          {item.status === 'waiting' && <> &middot; Queued</>}
          {item.status === 'failed' && <> &middot; Failed</>}
          {item.status === 'deleting' && <> &middot; Removing...</>}
        </span>
      </div>
      <div className="cap-upload-bar-wrap">
        <div className="cap-upload-bar" style={{ width: `${pct}%` }} />
      </div>
      <div className="cap-upload-actions">
        {item.status === 'failed' && onRetry && (
          <button className="cap-retry-btn" onClick={() => onRetry(item.id)} data-testid={`cap-retry-${item.id}`}>
            <AlertCircle size={12} /> Retry
          </button>
        )}
        {item.status !== 'failed' && <span className="cap-upload-pct">{pct}%</span>}
        {canDelete && onDelete && (
          <button className="cap-delete-btn" onClick={() => onDelete(item)} data-testid={`cap-delete-${item.id}`}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}


// ── Step 3: Capture Mode — Film/Upload per room ──
function CaptureMode({ rooms, selectedRooms, allRooms, session, uploadQueue, onAddFiles, onFilmNow, activeRoom, onSwitchRoom, onRetry, onAddRoom, onDelete, onReshoot, onRemoveRoom, onUploadAnyway }) {
  const [showAddRoom, setShowAddRoom] = useState(false);
  const availableRooms = allRooms.filter(r => !selectedRooms.includes(r.key));
  const completedClips = (roomKey) => {
    return uploadQueue.filter(u => u.roomKey === roomKey && u.status === 'complete').length;
  };
  const activeUploads = uploadQueue.filter(u => u.status === 'uploading' || u.status === 'waiting').length;

  return (
    <div className="cap-capture" data-testid="cap-capture-mode">
      {/* Room tabs — horizontal scroll */}
      <div className="cap-room-tabs">
        {selectedRooms.map(rk => {
          const room = allRooms.find(r => r.key === rk);
          const Icon = ROOM_ICONS[room?.icon] || Video;
          const clips = completedClips(rk);
          const isActive = activeRoom === rk;
          const uploading = uploadQueue.some(u => u.roomKey === rk && (u.status === 'uploading' || u.status === 'waiting'));
          const hasClips = uploadQueue.some(u => u.roomKey === rk);
          return (
            <div key={rk} className="cap-tab-wrap">
              <button
                className={`cap-room-tab ${isActive ? 'cap-tab-active' : ''} ${uploading ? 'cap-tab-uploading' : ''}`}
                onClick={() => onSwitchRoom(rk)}
                data-testid={`cap-tab-${rk}`}
              >
                <Icon size={16} />
                <span>{room?.name}</span>
                {clips > 0 && <span className="cap-tab-count">{clips}</span>}
                {uploading && <Loader2 size={12} className="spin cap-tab-spinner" />}
              </button>
              {!hasClips && (
                <button
                  className="cap-tab-remove"
                  onClick={(e) => { e.stopPropagation(); onRemoveRoom(rk); }}
                  data-testid={`cap-remove-${rk}`}
                >
                  <X size={10} />
                </button>
              )}
            </div>
          );
        })}
        {availableRooms.length > 0 && (
          <button
            className="cap-room-tab cap-tab-add"
            onClick={() => setShowAddRoom(!showAddRoom)}
            data-testid="cap-add-room-btn"
          >
            <Plus size={16} />
          </button>
        )}
      </div>

      {/* Add room dropdown */}
      {showAddRoom && (
        <div className="cap-add-room-list" data-testid="cap-add-room-list">
          {availableRooms.map(room => {
            const Icon = ROOM_ICONS[room.icon] || Video;
            return (
              <button
                key={room.key}
                className="cap-add-room-item"
                onClick={() => { onAddRoom(room.key); setShowAddRoom(false); }}
                data-testid={`cap-add-${room.key}`}
              >
                <Icon size={16} />
                <span>{room.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Active room content */}
      <div className="cap-room-content">
        <div className="cap-room-actions">
          <label className="cap-action-btn cap-film-btn" data-testid="cap-film-btn">
            <Camera size={22} />
            <span>Film Now</span>
            <input
              type="file"
              accept="video/*"
              hidden
              onChange={e => { onFilmNow(activeRoom, e.target.files); e.target.value = ''; }}
            />
          </label>
          <label className="cap-action-btn cap-upload-btn" data-testid="cap-choose-btn">
            <FolderOpen size={22} />
            <span>Camera Roll</span>
            <input
              type="file"
              accept="video/*"
              multiple
              hidden
              onChange={e => { onAddFiles(activeRoom, e.target.files); e.target.value = ''; }}
            />
          </label>
        </div>

        {/* Upload queue for this room */}
        <div className="cap-room-uploads">
          {uploadQueue.filter(u => u.roomKey === activeRoom).length === 0 && (
            <div className="cap-room-empty">
              <Video size={28} />
              <span>No clips yet — film or upload to get started</span>
            </div>
          )}
          {uploadQueue.filter(u => u.roomKey === activeRoom).map(item => (
            <UploadItem key={item.id} item={item} onRetry={onRetry} onDelete={onDelete} onReshoot={onReshoot} onUploadAnyway={onUploadAnyway} />
          ))}
        </div>
      </div>

      {/* Global upload status bar */}
      {activeUploads > 0 && (
        <div className="cap-global-status" data-testid="cap-global-status">
          <Loader2 size={14} className="spin" />
          <span>{activeUploads} upload{activeUploads !== 1 ? 's' : ''} in progress</span>
        </div>
      )}
    </div>
  );
}


// ── Main Capture App ──
export default function CaptureApp() {
  const [step, setStep] = useState('select'); // select | rooms | capture | done
  const [rooms, setRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [session, setSession] = useState(null);
  const [property, setProperty] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [speedMbps, setSpeedMbps] = useState(null);
  const [speedTesting, setSpeedTesting] = useState(true);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [wifiDismissed, setWifiDismissed] = useState(false);
  const processingRef = useRef(false);
  const queueRef = useRef([]);

  // Keep queueRef in sync
  useEffect(() => { queueRef.current = uploadQueue; }, [uploadQueue]);

  // Load rooms on mount
  useEffect(() => {
    axios.get(`${API}/api/capture/rooms`).then(r => setRooms(r.data.rooms || []));
  }, []);

  // Proper speed test on mount — download 500KB and measure
  useEffect(() => {
    const runSpeedTest = async () => {
      setSpeedTesting(true);
      const mbps = await measureSpeed();
      setSpeedMbps(mbps);
      setSpeedTesting(false);
      // Auto-show WiFi warning if slow
      if (mbps !== null && mbps < SPEED_THRESHOLD_MBPS) {
        setShowWifiModal(true);
      }
    };
    runSpeedTest();
  }, []);

  // Auto-retest speed after WiFi modal is dismissed
  useEffect(() => {
    if (wifiDismissed) {
      const timer = setTimeout(async () => {
        setSpeedTesting(true);
        const mbps = await measureSpeed();
        setSpeedMbps(mbps);
        setSpeedTesting(false);
        setWifiDismissed(false);
      }, 3000); // 3s delay — gives time for WiFi to connect
      return () => clearTimeout(timer);
    }
  }, [wifiDismissed]);

  // Warn before leaving if uploads in progress
  useEffect(() => {
    const handler = (e) => {
      const active = queueRef.current.some(u => u.status === 'uploading' || u.status === 'waiting');
      if (active) {
        e.preventDefault();
        e.returnValue = 'Uploads are still in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Property selected — create session
  const handlePropertySelect = async (client, branch, prop) => {
    setProperty(prop);
    try {
      const res = await axios.post(`${API}/api/capture/session`, {
        client_id: client.id,
        client_name: client.name,
        branch_id: branch.id,
        branch_name: branch.name,
        wms_job_id: String(prop.order_id),
        property_address: prop.address,
      });
      setSession(res.data);
      setStep('rooms');
    } catch {
      toast.error('Failed to create session');
    }
  };

  // Toggle room selection
  const toggleRoom = (key) => {
    setSelectedRooms(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Start filming — enter capture mode
  const startCapture = () => {
    if (selectedRooms.length === 0) return;
    setActiveRoom(selectedRooms[0]);
    setStep('capture');
  };

  // Process upload queue
  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (true) {
      const queue = queueRef.current;
      const next = queue.find(u => u.status === 'waiting');
      if (!next) break;

      // Mark as uploading
      setUploadQueue(prev => prev.map(u => u.id === next.id ? { ...u, status: 'uploading' } : u));

      try {
        // 1. Init upload
        const initFd = new FormData();
        initFd.append('session_id', session.session_id);
        initFd.append('room_key', next.roomKey);
        initFd.append('filename', next.filename);
        initFd.append('total_chunks', String(next.totalChunks));
        initFd.append('total_size', String(next.size));
        const initRes = await axios.post(`${API}/api/capture/init-upload`, initFd);
        const uploadId = initRes.data.upload_id;

        // 2. Upload chunks with retry per chunk
        const file = next.file;
        let uploadedBytes = 0;
        const startTime = performance.now();
        const MAX_RETRIES = 3;

        for (let i = 0; i < next.totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);

          let chunkSuccess = false;
          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            try {
              const chunkFd = new FormData();
              chunkFd.append('upload_id', uploadId);
              chunkFd.append('chunk_number', String(i));
              chunkFd.append('chunk', blob, `chunk_${i}`);
              await axios.post(`${API}/api/capture/upload-chunk`, chunkFd);
              chunkSuccess = true;
              break;
            } catch (chunkErr) {
              console.warn(`Chunk ${i} attempt ${attempt + 1} failed:`, chunkErr.message);
              if (attempt < MAX_RETRIES - 1) {
                await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
              }
            }
          }

          if (!chunkSuccess) {
            throw new Error(`Chunk ${i} failed after ${MAX_RETRIES} attempts`);
          }

          uploadedBytes += (end - start);
          const elapsed = (performance.now() - startTime) / 1000;
          const speed = uploadedBytes / elapsed;
          const remaining = (file.size - uploadedBytes) / speed;

          setUploadQueue(prev => prev.map(u => u.id === next.id ? {
            ...u,
            chunksUploaded: i + 1,
            speed,
            eta: remaining,
          } : u));
        }

        // 3. Complete upload
        const completeFd = new FormData();
        completeFd.append('upload_id', uploadId);
        await axios.post(`${API}/api/capture/complete-upload`, completeFd);

        setUploadQueue(prev => prev.map(u => u.id === next.id ? {
          ...u,
          status: 'complete',
          uploadId: uploadId,
          chunksUploaded: next.totalChunks,
          speed: 0,
          eta: 0,
        } : u));

        toast.success(`${next.filename} uploaded`);
      } catch (err) {
        console.error('Upload error:', err);
        // Extract the most useful reason we can for the user
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.detail || err?.response?.data?.message;
        const reason = serverMsg
          ? `${status ? status + ': ' : ''}${serverMsg}`
          : (status ? `HTTP ${status}` : (err?.message || 'Network error'));
        setUploadQueue(prev => prev.map(u => u.id === next.id ? {
          ...u,
          status: 'failed',
          errorReason: reason,
        } : u));
        toast.error(`${next.filename}: ${reason}`);
      }
    }

    processingRef.current = false;
  }, [session]);

  // Retry a failed upload
  const retryUpload = useCallback((itemId) => {
    setUploadQueue(prev => prev.map(u =>
      u.id === itemId ? { ...u, status: 'waiting', chunksUploaded: 0, speed: 0, eta: 0 } : u
    ));
    setTimeout(() => processQueue(), 100);
  }, [processQueue]);

  // Add a room during capture mode
  const addRoom = useCallback((roomKey) => {
    if (!selectedRooms.includes(roomKey)) {
      setSelectedRooms(prev => [...prev, roomKey]);
      setActiveRoom(roomKey);
    }
  }, [selectedRooms]);

  // Remove a room during capture mode (only if no clips attached)
  const removeRoom = useCallback((roomKey) => {
    setSelectedRooms(prev => {
      const updated = prev.filter(k => k !== roomKey);
      if (activeRoom === roomKey && updated.length > 0) {
        setActiveRoom(updated[0]);
      }
      return updated;
    });
  }, [activeRoom]);

  // Delete an uploaded clip (removes from R2 if completed)
  const deleteUpload = useCallback(async (item) => {
    if (item.status === 'waiting') {
      // Not yet uploaded — just remove from queue
      setUploadQueue(prev => prev.filter(u => u.id !== item.id));
      return;
    }
    if (item.status === 'failed') {
      // Failed — just remove from queue
      setUploadQueue(prev => prev.filter(u => u.id !== item.id));
      return;
    }
    // Completed — delete from R2 via backend
    setUploadQueue(prev => prev.map(u => u.id === item.id ? { ...u, status: 'deleting' } : u));
    try {
      await axios.delete(`${API}/api/capture/upload/${item.uploadId || item.id}`, {
        params: { session_id: session?.session_id }
      });
      setUploadQueue(prev => prev.filter(u => u.id !== item.id));
      toast.success(`${item.filename} removed`);
    } catch (err) {
      console.error('Delete error:', err);
      setUploadQueue(prev => prev.map(u => u.id === item.id ? { ...u, status: 'complete' } : u));
      toast.error('Failed to remove clip');
    }
  }, [session]);

  // Add files to upload queue
  const addFiles = useCallback(async (roomKey, files) => {
    if (!files || files.length === 0) return;
    const fileArr = Array.from(files);

    for (const file of fileArr) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const duration = await getVideoDuration(file);

      if (duration !== null && duration < MIN_DURATION) {
        setUploadQueue(prev => [...prev, {
          id, roomKey, filename: file.name, size: file.size, file, duration,
          totalChunks: 0, chunksUploaded: 0, speed: 0, eta: 0,
          status: 'rejected',
          rejectReason: 'Minimum Clip Length Not Met',
        }]);
        continue;
      }
      if (duration !== null && duration > MAX_DURATION) {
        setUploadQueue(prev => [...prev, {
          id, roomKey, filename: file.name, size: file.size, file, duration,
          totalChunks: 0, chunksUploaded: 0, speed: 0, eta: 0,
          status: 'rejected',
          rejectReason: 'Clip Too Long',
        }]);
        continue;
      }

      setUploadQueue(prev => [...prev, {
        id, roomKey, filename: file.name, size: file.size, file, duration,
        totalChunks: Math.ceil(file.size / CHUNK_SIZE),
        chunksUploaded: 0, status: 'waiting', speed: 0, eta: 0,
      }]);
    }
    setTimeout(() => processQueue(), 100);
  }, [processQueue]);

  // Reshoot: replace a rejected clip with a new file
  const reshootClip = useCallback(async (rejectedItem, files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const duration = await getVideoDuration(file);

    if (duration !== null && duration < MIN_DURATION) {
      setUploadQueue(prev => prev.map(u => u.id === rejectedItem.id ? {
        ...u, filename: file.name, size: file.size, file, duration,
        status: 'rejected', rejectReason: 'Minimum Clip Length Not Met',
      } : u));
      return;
    }
    if (duration !== null && duration > MAX_DURATION) {
      setUploadQueue(prev => prev.map(u => u.id === rejectedItem.id ? {
        ...u, filename: file.name, size: file.size, file, duration,
        status: 'rejected', rejectReason: 'Clip Too Long',
      } : u));
      return;
    }

    // Valid — replace with a waiting upload
    setUploadQueue(prev => prev.map(u => u.id === rejectedItem.id ? {
      ...u, filename: file.name, size: file.size, file, duration,
      totalChunks: Math.ceil(file.size / CHUNK_SIZE),
      chunksUploaded: 0, status: 'waiting', speed: 0, eta: 0,
      rejectReason: undefined,
    } : u));
    setTimeout(() => processQueue(), 100);
  }, [processQueue]);

  // Override: upload a rejected short clip anyway (user acknowledges < MIN_DURATION)
  const uploadAnyway = useCallback((rejectedItem) => {
    if (!rejectedItem?.file) return;
    setUploadQueue(prev => prev.map(u => u.id === rejectedItem.id ? {
      ...u,
      totalChunks: Math.ceil(u.size / CHUNK_SIZE),
      chunksUploaded: 0,
      status: 'waiting',
      speed: 0,
      eta: 0,
      rejectReason: undefined,
      overrideShort: true,
    } : u));
    setTimeout(() => processQueue(), 100);
  }, [processQueue]);

  // Complete/Summary
  const completedCount = uploadQueue.filter(u => u.status === 'complete').length;
  const totalCount = uploadQueue.length;
  const allDone = totalCount > 0 && uploadQueue.every(u => u.status === 'complete' || u.status === 'failed');

  return (
    <div className="cap-app" data-testid="capture-app">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="cap-header">
        <div className="cap-logo">
          <Camera size={22} />
          <span>NicheCam</span>
        </div>
        <SpeedIndicator
          mbps={speedMbps}
          testing={speedTesting}
          onTap={() => setShowWifiModal(true)}
        />
      </header>

      {/* WiFi Warning Modal */}
      {showWifiModal && (
        <WifiWarningModal
          mbps={speedMbps}
          onDone={() => { setShowWifiModal(false); setWifiDismissed(true); }}
        />
      )}

      {/* Property info bar */}
      {session && (
        <div className="cap-property-bar" data-testid="cap-property-bar">
          <span className="cap-property-address">{session.property_address}</span>
          <span className="cap-property-meta">{session.branch_name} &middot; #{session.wms_job_id}</span>
        </div>
      )}

      {/* Steps */}
      {step === 'select' && <PropertySelector onSelect={handlePropertySelect} />}

      {step === 'rooms' && (
        <RoomGrid
          rooms={rooms}
          selectedRooms={selectedRooms}
          onToggle={toggleRoom}
          onStart={startCapture}
        />
      )}

      {step === 'capture' && (
        <CaptureMode
          rooms={rooms}
          selectedRooms={selectedRooms}
          allRooms={rooms}
          session={session}
          uploadQueue={uploadQueue}
          onAddFiles={addFiles}
          onFilmNow={(roomKey, files) => addFiles(roomKey, files)}
          activeRoom={activeRoom}
          onSwitchRoom={setActiveRoom}
          onRetry={retryUpload}
          onAddRoom={addRoom}
          onDelete={deleteUpload}
          onReshoot={reshootClip}
          onRemoveRoom={removeRoom}
          onUploadAnyway={uploadAnyway}
        />
      )}

      {/* Done overlay */}
      {step === 'capture' && allDone && totalCount > 0 && (
        <div className="cap-done-banner" data-testid="cap-done-banner">
          <Check size={20} />
          <span>{completedCount} clip{completedCount !== 1 ? 's' : ''} uploaded successfully</span>
        </div>
      )}
    </div>
  );
}
